class PremiumShopSlider extends HTMLElement {
  constructor() {
    super();
    this.grid = this.querySelector('.premium-shop__carousel');
    this.items = this.querySelectorAll('.premium-shop__card');
    this.currentCenterIndex = 0;
    this.isUserScrolling = false;
    this.scrollTimeout = null;
    this.videoPlayers = [];
    this.currentPlayingVideo = null;
    
    if (this.grid && this.items.length > 0) {
      // Delay initialization slightly to prevent interfering with page load
      // Only initialize if section is actually in viewport or after page load
      if (document.readyState === 'complete') {
        this.initialize();
      } else {
        window.addEventListener('load', () => {
          // Small delay to ensure page hasn't scrolled
          setTimeout(() => {
            this.initialize();
          }, 100);
        }, { once: true });
      }
    }
  }

  initialize() {
    this.initScrollEffects();
    this.initVideoHandlers();
    // Start with middle card centered (only horizontal scroll, not page scroll)
    const middleIndex = Math.floor(this.items.length / 2);
    this.scrollToCenter(middleIndex, false);
    this.handleIntersection();
  }

  initScrollEffects() {
    // Simple center detection for video playback - no transforms
    this.grid.addEventListener('scroll', () => {
      this.isUserScrolling = true;
      
      // Clear existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      
      // After scroll ends, snap to center
      this.scrollTimeout = setTimeout(() => {
        this.snapToCenter();
        this.isUserScrolling = false;
      }, 150);
      
      requestAnimationFrame(() => {
        this.updateCenterDetection();
      });
    });
    
    // Initial update
    this.updateCenterDetection();
    
    // Update on resize
    window.addEventListener('resize', () => {
      requestAnimationFrame(() => {
        this.updateCenterDetection();
        this.scrollToCenter(this.currentCenterIndex, false);
      });
    });
  }

  updateCenterDetection() {
    if (!this.grid || this.items.length === 0) return;
    
    const scrollLeft = this.grid.scrollLeft;
    const itemWidth = this.items[0]?.offsetWidth || 320;
    const containerCenter = this.grid.clientWidth / 2;
    const scrollCenter = scrollLeft + containerCenter;

    let newCenterIndex = -1;
    let minDistance = Infinity;

    this.items.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const itemCenter = itemLeft + itemWidth / 2;
      
      // Calculate distance from center
      const distance = Math.abs(itemCenter - scrollCenter);
      const maxDistance = this.grid.clientWidth / 2;
      const normalizedDistance = distance / maxDistance;
      
      // Find the closest card to center
      if (distance < minDistance) {
        minDistance = distance;
        newCenterIndex = index;
      }
      
      // Apply center class if within threshold
      const isCenter = normalizedDistance < 0.3;
      if (isCenter) {
        item.classList.add('premium-shop__card--center');
      } else {
        item.classList.remove('premium-shop__card--center');
        this.pauseVideoAtIndex(index);
      }
    });

    // Play video for new center card
    if (newCenterIndex !== -1 && newCenterIndex !== this.currentCenterIndex) {
      this.currentCenterIndex = newCenterIndex;
      // Small delay to ensure smooth transition
      setTimeout(() => {
        this.playCenterVideo();
      }, 300);
    }
  }

  pauseVideoAtIndex(index) {
    const item = this.items[index];
    if (!item) return;
    
    const deferredMedia = item.querySelector('.premium-shop__deferred-media');
    if (!deferredMedia) return;
    
    if (deferredMedia.hasAttribute('loaded')) {
      const videoElement = deferredMedia.querySelector('video');
      if (videoElement && videoElement.played) {
        videoElement.pause();
      }
    }
  }

  snapToCenter() {
    if (!this.grid || this.items.length === 0) return;
    
    const scrollLeft = this.grid.scrollLeft;
    const itemWidth = this.items[0]?.offsetWidth || 320;
    const containerCenter = this.grid.clientWidth / 2;
    const scrollCenter = scrollLeft + containerCenter;
    
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    this.items.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const itemCenter = itemLeft + itemWidth / 2;
      const distance = Math.abs(itemCenter - scrollCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    // Only snap if not already centered
    if (closestIndex !== this.currentCenterIndex) {
      this.scrollToCenter(closestIndex, true);
    }
  }

  scrollToCenter(index, smooth = true) {
    if (!this.grid || !this.items[index]) return;
    
    const item = this.items[index];
    const itemWidth = item.offsetWidth;
    const containerCenter = this.grid.clientWidth / 2;
    const itemCenter = item.offsetLeft + itemWidth / 2;
    const scrollLeft = itemCenter - containerCenter;
    
    this.grid.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: smooth ? 'smooth' : 'auto'
    });
    
    // Update center index immediately for visual feedback
    this.currentCenterIndex = index;
    
    // Update center detection and play video after scroll
    if (smooth) {
      setTimeout(() => {
        this.updateCenterDetection();
        this.playCenterVideo();
      }, 500);
    } else {
      // Small delay for initial load to ensure layout is ready
      setTimeout(() => {
        this.updateCenterDetection();
        this.playCenterVideo();
      }, 200);
    }
  }

  initVideoHandlers() {
    // Find all video elements
    this.items.forEach((item, index) => {
      const deferredMedia = item.querySelector('.premium-shop__deferred-media');
      if (deferredMedia) {
        const videoElement = deferredMedia.querySelector('video');
        const iframe = deferredMedia.querySelector('iframe');
        const playButton = deferredMedia.querySelector('.premium-shop__play-button');
        
        if (videoElement) {
          this.videoPlayers.push({
            element: videoElement,
            index: index,
            item: item,
            deferredMedia: deferredMedia,
            playButton: playButton
          });
          
          // Set video to muted
          videoElement.muted = true;
          
          // Listen for video events
          videoElement.addEventListener('play', () => {
            this.onVideoPlay(index);
          });
          
          videoElement.addEventListener('pause', () => {
            this.onVideoPause(index);
          });
          
          videoElement.addEventListener('ended', () => {
            this.onVideoEnd(index);
          });
          
          videoElement.addEventListener('playing', () => {
            this.onVideoPlay(index);
          });
        } else if (iframe) {
          // For YouTube/Vimeo iframes, we'll handle them differently
          this.videoPlayers.push({
            element: iframe,
            index: index,
            item: item,
            deferredMedia: deferredMedia,
            playButton: playButton,
            isIframe: true
          });
        }
      }
    });
  }

  onVideoPlay(index) {
    const videoData = this.videoPlayers.find(v => v.index === index);
    if (videoData) {
      const deferredMedia = videoData.deferredMedia;
      if (deferredMedia) {
        deferredMedia.classList.add('video-playing');
        deferredMedia.classList.remove('video-paused');
      }
      if (videoData.playButton) {
        videoData.playButton.style.display = 'none';
        videoData.playButton.style.opacity = '0';
      }
    }
  }

  onVideoPause(index) {
    const videoData = this.videoPlayers.find(v => v.index === index);
    if (videoData) {
      const deferredMedia = videoData.deferredMedia;
      if (deferredMedia) {
        deferredMedia.classList.remove('video-playing');
        deferredMedia.classList.add('video-paused');
      }
      if (videoData.playButton) {
        videoData.playButton.style.display = 'flex';
        videoData.playButton.style.opacity = '1';
      }
    }
  }

  playCenterVideo() {
    // Pause all videos first
    this.pauseAllVideos();
    
    const centerItem = this.items[this.currentCenterIndex];
    if (!centerItem) return;
    
    const deferredMedia = centerItem.querySelector('.premium-shop__deferred-media');
    if (!deferredMedia) return;
    
    // Check if video is already loaded
    if (deferredMedia.hasAttribute('loaded')) {
      const videoElement = deferredMedia.querySelector('video');
      const iframe = deferredMedia.querySelector('iframe');
      
      if (videoElement) {
        // Ensure video is muted
        videoElement.muted = true;
        videoElement.play().catch(err => {
          console.log('Video autoplay prevented:', err);
        });
        this.currentPlayingVideo = this.currentCenterIndex;
        // Hide play button
        this.onVideoPlay(this.currentCenterIndex);
      } else if (iframe) {
        // For iframes, we can't control playback directly
        this.currentPlayingVideo = this.currentCenterIndex;
        const playButton = deferredMedia.querySelector('.premium-shop__play-button');
        if (playButton) {
          playButton.style.display = 'none';
        }
      }
    } else {
      // Video not loaded yet: load without focus so the page doesn't scroll to this section
      if (typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
        const videoElement = deferredMedia.querySelector('video');
        const iframe = deferredMedia.querySelector('iframe');
        if (videoElement) {
          videoElement.muted = true;
          videoElement.play().catch(err => {
            console.log('Video autoplay prevented:', err);
          });
          this.currentPlayingVideo = this.currentCenterIndex;
          this.onVideoPlay(this.currentCenterIndex);
        } else if (iframe) {
          this.currentPlayingVideo = this.currentCenterIndex;
          const playButton = deferredMedia.querySelector('.premium-shop__play-button');
          if (playButton) playButton.style.display = 'none';
        }
      } else {
        const posterButton = deferredMedia.querySelector('.premium-shop__poster');
        if (posterButton) {
          posterButton.click();
          const videoElement = deferredMedia.querySelector('video');
          const iframe = deferredMedia.querySelector('iframe');
          if (videoElement) {
            videoElement.muted = true;
            videoElement.play().catch(() => {});
            this.currentPlayingVideo = this.currentCenterIndex;
            this.onVideoPlay(this.currentCenterIndex);
          } else if (iframe) {
            this.currentPlayingVideo = this.currentCenterIndex;
            const playButton = deferredMedia.querySelector('.premium-shop__play-button');
            if (playButton) playButton.style.display = 'none';
          }
        }
      }
    }
  }

  pauseAllVideos() {
    this.videoPlayers.forEach(({ element, isIframe }) => {
      if (isIframe) {
        // For iframes, we can't pause directly
        return;
      }
      
      if (element && element.pause) {
        element.pause();
      }
    });
  }

  onVideoEnd(currentIndex) {
    // Show play button when video ends
    this.onVideoPause(currentIndex);
    
    // Move to next video
    const nextIndex = (currentIndex + 1) % this.items.length;
    
    // Scroll to next with smooth animation
    this.scrollToCenter(nextIndex, true);
    
    // Update center detection after scroll
    setTimeout(() => {
      this.updateCenterDetection();
    }, 100);
  }

  handleIntersection() {
    // Play center video when section is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Small delay to ensure layout is ready
          setTimeout(() => {
            this.playCenterVideo();
          }, 300);
        } else {
          // Pause all videos when section is not visible
          this.pauseAllVideos();
        }
      });
    }, { threshold: 0.3 });

    observer.observe(this);
  }

  disconnectedCallback() {
    this.pauseAllVideos();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }
}

customElements.define('premium-shop-slider', PremiumShopSlider);
